import { useLayoutEffect, useState } from 'react'
import { fetchTimecodedCues } from './transcriptionApi'
import { Cue } from './webvtt'

/**
 * Fetches + caches WebVTT cues for a timecoded media item. `cues` is [] when not
 * timecoded or while loading; `loading` is true only while a timecoded track's
 * cues are in flight, so a consumer can tell "still fetching" from "no cues" and
 * avoid flashing the static transcript before the cues arrive.
 */
export const useTimecodedCues = (
	mediaItemId: number | undefined,
	transcriptionFormat: string | undefined,
	language: string,
): { cues: Cue[]; loading: boolean } => {
	const [cues, setCues] = useState<Cue[]>([])
	const [loading, setLoading] = useState(false)

	// useLayoutEffect (not useEffect) so the loading flip is flushed before the
	// browser paints: on mount of a timecoded track — and on an in-place html→timecoded
	// track switch — a passive effect would run only after the first paint, so the
	// static-transcript branch would flash for one frame before loading became true.
	useLayoutEffect(() => {
		let active = true
		setCues([]) // drop stale cues immediately on any change (e.g. track switch) so the old transcript doesn't linger
		if (transcriptionFormat !== 'timecoded' || !mediaItemId) {
			setLoading(false)
			return
		}
		setLoading(true)
		fetchTimecodedCues(mediaItemId, language).then(c => {
			if (active) {
				setCues(c)
				setLoading(false)
			}
		})
		return () => {
			active = false
		}
	}, [mediaItemId, transcriptionFormat, language])

	return { cues, loading }
}
